import type { Node } from '@xyflow/react'
import { useSeriesDataStore } from '@/stores/useSeriesDataStore'

/**
 * Node State Filter - Excludes transient/ephemeral fields from persistence
 *
 * **Purpose:**
 * Remove temporary execution state (locked, orchestrator status) that should
 * NOT be persisted to database. When user reloads page, nodes should be in
 * an editable state, not stuck in "generating" or "locked" state. Failed
 * outputs retain their terminal status so reloading does not hide failures.
 *
 * **Filtered Fields:**
 * - `locked`: Temporary lock during AI generation
 * - `orchestratorState`: Local adapter state (status, progress, message)
 * - `_action`: Transient programmatic actions
 *
 * **Usage:**
 * ```typescript
 * const cleanNodes = filterNodeTransientState(nodes)
 * saveNodes(cleanNodes) // Save only persistent state
 * ```
 */

/**
 * Transient fields that should NOT be persisted
 */
const TRANSIENT_FIELDS = [
  'locked',             // Temporary lock during generation
  'orchestratorState',  // Local adapter state (NOT part of GenieNodeState schema)
  '_action',            // Programmatic actions (transient, cleared after execution)
  'generationStatus',   // Runtime status; terminal errors are preserved below
  'generationProgress', // Placeholder node generation progress (0-100)
] as const

/**
 * Return a copy of a node state object without transient fields
 */
export function stripTransientStateFields(state: Record<string, unknown>): Record<string, unknown> {
  const cleanState = { ...state }

  for (const field of TRANSIENT_FIELDS) {
    // A failed output is a saved result, not an in-flight execution state.
    if (field === 'generationStatus' && state[field] === 'error') continue
    delete cleanState[field]
  }

  return cleanState
}

/**
 * Filter transient state from a single node
 */
function filterNodeTransientState(node: Node): Node {
  // State is in node.data.state (React Flow structure)
  const nodeData = node.data as Record<string, unknown> | undefined
  const componentState = nodeData?.state as Record<string, unknown> | undefined
  if (!componentState) return node

  // Remove transient fields from state
  const cleanState = stripTransientStateFields(componentState)

  return {
    ...node,
    selected: false,
    data: {
      ...node.data,
      state: cleanState,
    },
  }
}

/**
 * Filter transient state from array of nodes
 */
export function filterNodesTransientState(nodes: Node[]): Node[] {
  return nodes.map(filterNodeTransientState)
}

/**
 * Check if a field is transient (should not be persisted)
 */
export function isTransientField(field: string): boolean {
  return (TRANSIENT_FIELDS as readonly string[]).includes(field)
}

/**
 * Inject large series data from the external Zustand store back into nodes
 * at save time. This keeps React Flow node state lightweight during runtime
 * while ensuring all data is persisted.
 */
export function injectSeriesDataForSave(nodes: Node[]): Node[] {
  return useSeriesDataStore.getState().injectForSave(nodes) as Node[]
}
